import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { db, auth } from '../../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import BackToDashboard from './BackToDashboard';
import { updateUserEmail, isFirebaseAdminConfigured } from '../../services/firebaseAdmin';

const PersonnelManagement = () => {
  const { currentUser, userData, createUser } = useAuth();
  
  // Liste des services disponibles
  const services = [
    'ADMINISTRATIF',
    'TECHNIQUE',
    'DIRECTION',
    'LINGERIE',
    'ATELIER',
    'SERVICE_GENERAL',
    'CUISINE',
    'JARDIN',
    'SA'
  ];

  // Fonction pour formater l'affichage des services
  const formatService = (service) => {
    if (!service) return 'Service non spécifié';
    
    switch (service) {
      case 'SERVICE_GENERAL':
        return 'Service Général';
      case 'SA':
        return 'SA';
      default:
        return service.charAt(0) + service.slice(1).toLowerCase();
    }
  };
  const [personnel, setPersonnel] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [overtimesData, setOvertimesData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'personnel',
    service: 'SERVICE_GENERAL',
    grade: '',
    status: 'actif',
    password: '',
    horaireDecale: false,
    observationHoraire: '',
    schedule: {
      Lundi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
      Mardi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
      Mercredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
      Jeudi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
      Vendredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
      Samedi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } }
    }
  });

  useEffect(() => {
    if (userData?.establishmentId) {
      fetchPersonnel();
    }
  }, [userData]);

  const fetchOvertimes = async () => {
    try {
      const overtimesRef = collection(db, 'overtimes');
      const q = query(
        overtimesRef,
        where('establishmentId', '==', userData.establishmentId)
      );
      const snapshot = await getDocs(q);
      
      // Calculer les totaux par personnel
      const totals = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const personnelId = data.personnelId;
        
        if (!totals[personnelId]) {
          totals[personnelId] = {
            totalHeures: 0,
            totalMajorees: 0,
            count: 0
          };
        }
        
        totals[personnelId].totalHeures += data.nombreHeures || 0;
        totals[personnelId].totalMajorees += (data.nombreHeures * (1 + data.tauxMajoration / 100)) || 0;
        totals[personnelId].count += 1;
      });
      
      setOvertimesData(totals);
    } catch (error) {
      console.error('Erreur lors de la récupération des heures supplémentaires:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const personnelRef = collection(db, 'users');
      const q = query(
        personnelRef,
        where('establishmentId', '==', userData.establishmentId)
      );
      const querySnapshot = await getDocs(q);
      const personnelList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPersonnel(personnelList);
      
      // Récupérer les heures supplémentaires
      await fetchOvertimes();
    } catch (error) {
      console.error('Erreur lors de la récupération du personnel:', error);
      showSnackbar('Erreur lors de la récupération du personnel', 'error');
    }
  };

  const handleOpenDialog = (person = null) => {
    if (person) {
      setEditingPersonnel(person);
      setFormData({
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        phone: person.phone,
        role: person.role,
        service: person.service || '',
        grade: person.grade || '',
        status: person.status,
        password: '',
        horaireDecale: person.horaireDecale || false,
        observationHoraire: person.observationHoraire || '',
        schedule: person.schedule || {
          Lundi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Mardi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Mercredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Jeudi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Vendredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Samedi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } }
        }
      });
    } else {
      setEditingPersonnel(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'personnel',
        service: '',
        grade: '',
        status: 'actif',
        password: '',
        horaireDecale: false,
        observationHoraire: '',
        schedule: {
          Lundi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Mardi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Mercredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Jeudi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Vendredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
          Samedi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } }
        }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPersonnel(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'personnel',
      service: '',
      grade: '',
      status: 'actif',
      password: '',
      schedule: {
        Lundi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
        Mardi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
        Mercredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
        Jeudi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
        Vendredi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } },
        Samedi: { active: false, tasks: [''], withBreak: false, continuous: { start: '', end: '' }, morning: { start: '', end: '' }, afternoon: { start: '', end: '' } }
      }
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Log des données utilisateur actuelles

      if (!userData?.establishmentId) {
        console.error('Erreur: ID établissement non trouvé');
        showSnackbar('Erreur: ID établissement non trouvé', 'error');
        return;
      }

      if (editingPersonnel) {
        // Mise à jour d'un membre du personnel existant
        const personnelRef = doc(db, 'users', editingPersonnel.id);
        const updateData = {
          ...formData,
          updatedAt: new Date(),
        };
        delete updateData.password;
        
        // Conserver l'emploi du temps pour les chefs de service, la direction RH et le personnel administratif
        if (formData.role !== 'chefService' && formData.role !== 'directionRH' && !(formData.role === 'personnel' && formData.service === 'ADMINISTRATIF')) {
          delete updateData.schedule;
        }
        
        // Vérifier si l'email a changé et si Firebase Admin est configuré
        const emailChanged = formData.email !== editingPersonnel.email;
        let firebaseAuthUpdateSuccess = false;
        
        if (emailChanged) {
          
          if (isFirebaseAdminConfigured()) {
            try {
              const result = await updateUserEmail(editingPersonnel.id, formData.email);
              if (result.success) {
                firebaseAuthUpdateSuccess = true;
                showSnackbar('Email mis à jour dans Firebase Auth', 'info');
              } else {
                console.error('Erreur Firebase Auth:', result.error);
                showSnackbar(`Erreur Firebase Auth: ${result.error}`, 'warning');
                // Continuer avec la mise à jour Firestore même si Firebase Auth échoue
              }
            } catch (error) {
              console.error('Erreur lors de la mise à jour Firebase Auth:', error);
              showSnackbar('Erreur lors de la mise à jour Firebase Auth', 'warning');
              // Continuer avec la mise à jour Firestore même si Firebase Auth échoue
            }
          } else {
            console.warn('Firebase Admin non configuré, email non mis à jour dans Firebase Auth');
            showSnackbar('⚠️ Firebase Admin non configuré. Email mis à jour uniquement dans la base de données.', 'warning');
          }
        }
        
        // Mettre à jour Firestore
        await updateDoc(personnelRef, updateData);
        
        // Message de succès approprié
        if (emailChanged && firebaseAuthUpdateSuccess) {
          showSnackbar('Membre du personnel mis à jour avec succès (y compris dans Firebase Auth)');
        } else if (emailChanged) {
          showSnackbar('Membre du personnel mis à jour avec succès (email mis à jour uniquement en base de données)');
        } else {
          showSnackbar('Membre du personnel mis à jour avec succès');
        }
      } else {
        // Vérification des champs requis
        const requiredFields = ['email', 'password', 'firstName', 'lastName', 'role', 'service', 'grade'];
        const missingFields = requiredFields.filter(field => !formData[field]);
        
        if (missingFields.length > 0) {
          console.error('Champs manquants:', missingFields);
          showSnackbar(`Champs requis manquants: ${missingFields.join(', ')}`, 'error');
          return;
        }

        if (!formData.password || formData.password.length < 6) {
          console.error('Mot de passe invalide. Longueur:', formData.password?.length);
          showSnackbar('Le mot de passe doit contenir au moins 6 caractères', 'error');
          return;
        }

        // Log des données du formulaire

        // Préparation des données utilisateur
        const newUserData = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          role: formData.role,
          service: formData.service,
          grade: formData.grade,
          establishmentId: userData.establishmentId,
          status: 'actif',
          phone: formData.phone || '',
          horaireDecale: formData.horaireDecale,
          observationHoraire: formData.horaireDecale ? formData.observationHoraire : '',
          createdBy: currentUser.uid,
        };
        
        // Ajouter l'emploi du temps si c'est un chef de service, un personnel administratif ou direction RH
        if (formData.role === 'chefService' || formData.role === 'directionRH' || (formData.role === 'personnel' && formData.service === 'ADMINISTRATIF')) {
          newUserData.schedule = formData.schedule;
        }


        let createdAuthUser = null;

        try {
          // 1. Créer d'abord le compte Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
          createdAuthUser = userCredential.user;

          // 2. Créer ensuite le document Firestore
          const userDocRef = doc(db, 'users', createdAuthUser.uid);
          await setDoc(userDocRef, {
            ...newUserData,
            uid: createdAuthUser.uid,
            createdAt: new Date(),
            updatedAt: new Date()
          });

          // 3. Vérifier que tout est bien créé
          const checkDoc = await getDoc(doc(db, 'users', createdAuthUser.uid));
          if (!checkDoc.exists()) {
            throw new Error('Le document utilisateur n\'a pas été créé dans Firestore');
          }

          showSnackbar('Nouveau membre du personnel ajouté avec succès');
          await fetchPersonnel();
        } catch (error) {
          console.error('Erreur détaillée lors de la création:', error);
          console.error('Code:', error.code);
          console.error('Message:', error.message);
          console.error('Stack:', error.stack);

          // Nettoyage en cas d'erreur
          if (error.code !== 'auth/email-already-in-use' && createdAuthUser) {
            try {
              await createdAuthUser.delete();
            } catch (cleanupError) {
              console.error('Erreur lors du nettoyage:', cleanupError);
            }
          }

          if (error.code === 'auth/email-already-in-use') {
            showSnackbar('Cette adresse email est déjà utilisée', 'error');
          } else if (error.code === 'auth/invalid-email') {
            showSnackbar('Adresse email invalide', 'error');
          } else if (error.code === 'auth/weak-password') {
            showSnackbar('Le mot de passe doit contenir au moins 6 caractères', 'error');
          } else {
            showSnackbar(error.message || 'Erreur lors de la création du compte', 'error');
          }
          return;
        }
      }

      handleCloseDialog();
    } catch (error) {
      console.error('=== Erreur globale dans handleSubmit ===');
      console.error('Type:', error.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);
      showSnackbar(error.message || 'Erreur lors de l\'enregistrement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre du personnel ?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'users', id));
        showSnackbar('Membre du personnel supprimé avec succès');
        fetchPersonnel();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showSnackbar('Erreur lors de la suppression', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredPersonnel = personnel.filter(person =>
    person.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackToDashboard />
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Gestion du Personnel
          </Typography>
          
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={loading}
              >
                Ajouter un membre
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Heures Sup.</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPersonnel.map((person) => (
                  <TableRow key={person.id}>
                    <TableCell>{`${person.firstName} ${person.lastName}`}</TableCell>
                    <TableCell>{person.email}</TableCell>
                    <TableCell>{person.phone}</TableCell>
                    <TableCell>{person.role}</TableCell>
                    <TableCell>{person.service}</TableCell>
                    <TableCell>{person.status}</TableCell>
                    <TableCell align="right">
                      {overtimesData[person.id] ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {overtimesData[person.id].totalHeures.toFixed(2)}h
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            ({overtimesData[person.id].totalMajorees.toFixed(2)}h majorées)
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          0h
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        onClick={() => handleOpenDialog(person)}
                        disabled={loading}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(person.id)}
                        disabled={loading}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingPersonnel ? 'Modifier un membre' : 'Ajouter un membre'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom"
                  name="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              {!editingPersonnel && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mot de passe"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    helperText="Minimum 6 caractères"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Rôle</InputLabel>
                  <Select
                    value={formData.role}
                    label="Rôle"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <MenuItem value="personnel">Personnel</MenuItem>
                    <MenuItem value="chefService">Chef de service</MenuItem>
                    <MenuItem value="directionRH">Direction RH</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={formData.service}
                    label="Service"
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  >
                    {services.map((service) => (
                      <MenuItem key={service} value={service}>
                        {formatService(service)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="actif">Actif</MenuItem>
                    <MenuItem value="inactif">Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.horaireDecale}
                      onChange={(e) => setFormData({ ...formData, horaireDecale: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Horaire décalé"
                />
              </Grid>
              
              {formData.horaireDecale && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observation (calcul spécifique)"
                    multiline
                    rows={3}
                    value={formData.observationHoraire}
                    onChange={(e) => setFormData({ ...formData, observationHoraire: e.target.value })}
                    placeholder="Précisez le calcul spécifique pour cet horaire décalé"
                  />
                </Grid>
              )}
              
              {/* Section Emploi du temps - pour les chefs de service, le personnel administratif et la direction RH */}
              {(formData.role === 'chefService' || formData.role === 'directionRH' || (formData.role === 'personnel' && formData.service === 'ADMINISTRATIF')) && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                    Emploi du temps
                  </Typography>
                  {Object.entries(formData.schedule).map(([day, daySchedule]) => (
                    <Accordion key={day}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{day}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={daySchedule.active}
                                  onChange={(e) => {
                                    const newSchedule = { ...formData.schedule };
                                    newSchedule[day].active = e.target.checked;
                                    setFormData({ ...formData, schedule: newSchedule });
                                  }}
                                />
                              }
                              label={daySchedule.active ? "Jour travaillé" : "Jour non travaillé"}
                            />
                          </Grid>

                          {daySchedule.active && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={daySchedule.withBreak}
                                    onChange={(e) => {
                                      const newSchedule = { ...formData.schedule };
                                      newSchedule[day].withBreak = e.target.checked;
                                      setFormData({ ...formData, schedule: newSchedule });
                                    }}
                                    color="primary"
                                  />
                                }
                                label={daySchedule.withBreak ? "Horaires avec pause" : "Horaires continus"}
                              />

                              {daySchedule.withBreak ? (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                      Matin
                                    </Typography>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Début"
                                          type="time"
                                          value={daySchedule.morning?.start || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].morning.start = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Fin"
                                          type="time"
                                          value={daySchedule.morning?.end || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].morning.end = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                      Après-midi
                                    </Typography>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Début"
                                          type="time"
                                          value={daySchedule.afternoon?.start || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].afternoon.start = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Fin"
                                          type="time"
                                          value={daySchedule.afternoon?.end || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].afternoon.end = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              ) : (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  <Grid item xs={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                      Horaires continus
                                    </Typography>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Début"
                                          type="time"
                                          value={daySchedule.continuous?.start || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].continuous.start = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          fullWidth
                                          label="Fin"
                                          type="time"
                                          value={daySchedule.continuous?.end || ''}
                                          onChange={(e) => {
                                            const newSchedule = { ...formData.schedule };
                                            newSchedule[day].continuous.end = e.target.value;
                                            setFormData({ ...formData, schedule: newSchedule });
                                          }}
                                          InputLabelProps={{ shrink: true }}
                                          size="small"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}

                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: 'text.secondary'
                                }}>
                                  <AssignmentIcon sx={{ fontSize: 18 }} />
                                  Tâches
                                </Typography>
                                {daySchedule.tasks?.map((task, taskIndex) => (
                                  <Box key={taskIndex} sx={{ 
                                    display: 'flex',
                                    gap: 1,
                                    mb: 1,
                                    alignItems: 'center'
                                  }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      value={task}
                                      onChange={(e) => {
                                        const newSchedule = { ...formData.schedule };
                                        newSchedule[day].tasks[taskIndex] = e.target.value;
                                        setFormData({ ...formData, schedule: newSchedule });
                                      }}
                                      placeholder={`Tâche ${taskIndex + 1}`}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newSchedule = { ...formData.schedule };
                                        newSchedule[day].tasks = newSchedule[day].tasks.filter((_, i) => i !== taskIndex);
                                        setFormData({ ...formData, schedule: newSchedule });
                                      }}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                ))}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    const newSchedule = { ...formData.schedule };
                                    newSchedule[day].tasks.push('');
                                    setFormData({ ...formData, schedule: newSchedule });
                                  }}
                                >
                                  Ajouter une tâche
                                </Button>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                editingPersonnel ? 'Modifier' : 'Ajouter'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default PersonnelManagement;
